import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Checkbox from "antd/es/checkbox";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import CustomRangePicker from "@app/component/input/dateRangeSelect/components/CustomRangePicker";
import DateRange from "@app/scene/reports/types/DateRange";
import { updateIntegrationSyncsViewState } from "@app/store/integrations/actions";
import { getIntegrationViewState } from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import IntegrationSyncsViewState from "@app/types/viewModel/IntegrationSyncsViewState";
import formatDateRange from "@app/util/stackRank/formatDateRange";

import { ALL_TIME } from "../utils/consts";

import styles from "./IntegrationTimeSelector.module.scss";

interface Props {
  updateIntegrationSyncsViewState: typeof updateIntegrationSyncsViewState.request;
  viewState: IntegrationSyncsViewState;
}

const messages = defineMessages({
  allTime: {
    id: "integration.allTime.rangeType.label",
    defaultMessage: "All-time",
    description: "A range type selectboxs title in Date Range dropdown on the Integration page",
  },
});

const IntegrationTimeSelector: React.FC<Props> = ({
  updateIntegrationSyncsViewState,
  viewState,
}) => {
  const intl = useIntl();
  const [allTime, , , toggleAllTime] = useBoolean(viewState.dateRangeOption === ALL_TIME);
  const [dropdownVisible, showDropdown, hideDropdown] = useBoolean();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(viewState.dateRange);

  const resetDateRange = useCallback(() => {
    hideDropdown();
    if (
      !allTime &&
      dateRange?.startDate === undefined &&
      dateRange?.endDate === undefined &&
      viewState.dateRangeOption
    ) {
      toggleAllTime();
    }
    if (
      !allTime &&
      dateRange?.startDate === undefined &&
      dateRange?.endDate === undefined &&
      viewState.dateRange
    ) {
      setDateRange(viewState.dateRange);
    }
  }, [toggleAllTime, allTime, dateRange, hideDropdown, viewState]);

  const setAllTimeRange = useCallback(() => {
    toggleAllTime();
  }, [toggleAllTime]);

  const handleApplyClick = useCallback(() => {
    if (allTime) {
      updateIntegrationSyncsViewState({
        viewState: { dateRange: undefined, dateRangeOption: ALL_TIME },
      });
      setDateRange(undefined);
    } else {
      updateIntegrationSyncsViewState({ viewState: { dateRange, dateRangeOption: undefined } });
    }
    hideDropdown();
  }, [allTime, updateIntegrationSyncsViewState, hideDropdown, dateRange, setDateRange]);

  return (
    <div
      className={cn(styles.displayDate, {
        [styles.allTimeSelected]: !viewState.dateRange,
        [styles.rangeSelected]: viewState.dateRange,
      })}
    >
      <div className={styles.allTime}>
        {viewState.dateRange
          ? formatDateRange(viewState.dateRange)
          : intl.formatMessage(messages.allTime)}
      </div>
      <Dropdown
        overlay={
          <div className={styles.container}>
            <div className={styles.title}>
              {intl.formatMessage({
                id: "Integration.rangeSelector.rangeType.label",
                defaultMessage: "Change date range",
                description:
                  "A range type selectboxs title in Date Range dropdown on the Integration page",
              })}
            </div>
            <Row gutter={[layout.spacerS, layout.spacerM]}>
              <Col span={24}>
                <CustomRangePicker disabled={allTime} onChange={setDateRange} value={dateRange} />
              </Col>
              <Col>
                <Checkbox checked={allTime} onChange={setAllTimeRange}>
                  {intl.formatMessage(messages.allTime)}
                </Checkbox>
              </Col>
              <Col span={24}>
                <Row gutter={layout.spacerS} justify="end">
                  <Col>
                    <Button onClick={resetDateRange}>
                      {intl.formatMessage({
                        id: "leaderboard.rangeSelector.cancel",
                        defaultMessage: "Cancel",
                        description: "Cancel button title in Leaderboard's date range selector",
                      })}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      disabled={
                        !allTime &&
                        dateRange?.startDate === undefined &&
                        dateRange?.endDate === undefined
                      }
                      onClick={handleApplyClick}
                      type="primary"
                    >
                      {intl.formatMessage({
                        id: "leaderboard.rangeSelector.apply",
                        defaultMessage: "Apply",
                        description: "Apply button title in Leaderboard's date range selector",
                      })}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        }
        visible={dropdownVisible}
      >
        <Button className={styles.changeButton} onClick={showDropdown} type="link">
          {intl.formatMessage({
            id: "leaderboard.rangeSelector.trigger",
            defaultMessage: "Change",
            description: "Title of the Leaderboard date range selector trigger button",
          })}
        </Button>
      </Dropdown>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  viewState: getIntegrationViewState(state),
});

const mapDispatchToProps = {
  updateIntegrationSyncsViewState: updateIntegrationSyncsViewState.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationTimeSelector);
